import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "@/lib/api";
import {
  CloudDownload, X, Loader2, CheckCircle2, AlertTriangle, ShieldCheck, Eye, EyeOff,
} from "lucide-react";

const PHASES = [
  ["login", "Sign in to production"],
  ["snapshot", "Request production data"],
  ["download", "Download production data"],
  ["verify", "Verify production data"],
  ["preview_backup", "Back up current preview data"],
  ["restore", "Replace preview data with production"],
];
const PHASE_ORDER = PHASES.map(([k]) => k).concat(["done"]);

export default function ProdPullModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [mode, setMode] = useState("form"); // form | running | done | error
  const [status, setStatus] = useState(null);
  const [startErr, setStartErr] = useState("");
  const jobRef = useRef(null);
  const pollRef = useRef(null);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => () => stopPolling(), []);

  const start = async () => {
    setStartErr("");
    try {
      const res = await api.post("/admin/prod-pull/start", { email, password });
      jobRef.current = res.data;
      setMode("running");
      setPassword("");
      pollRef.current = setInterval(async () => {
        try {
          const { job_id, job_token } = jobRef.current;
          const s = await api.get(`/admin/prod-pull/status?job=${job_id}&t=${job_token}`);
          setStatus(s.data);
          if (!s.data.running) {
            stopPolling();
            setMode(s.data.error ? "error" : "done");
          }
        } catch { /* transient — keep polling */ }
      }, 2000);
    } catch (e) {
      setStartErr(formatApiError(e?.response?.data?.detail) || "Could not start the pull.");
    }
  };

  const canStart = email.trim() && password && confirmText.trim().toUpperCase() === "REPLACE";
  const phaseIdx = status ? PHASE_ORDER.indexOf(status.phase) : -1;
  const summary = status?.summary;

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: "rgba(10,12,28,0.55)" }} data-testid="prod-pull-modal">
      <div className="w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden" style={{ background: "#FFFFFF", border: "1px solid rgba(15,18,38,0.1)" }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid rgba(15,18,38,0.08)" }}>
          <div className="flex items-center gap-2.5">
            <CloudDownload size={18} style={{ color: "#4060FF" }} />
            <div>
              <div className="font-semibold text-[15px]" style={{ color: "#0F1226" }}>Pull from production</div>
              <div className="text-[11px]" style={{ color: "#64748B" }}>botchaincp.com → this preview · production is read-only</div>
            </div>
          </div>
          {mode !== "running" && (
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5" data-testid="prod-pull-close"><X size={16} /></button>
          )}
        </div>

        <div className="px-6 py-5">
          {mode === "form" && (
            <div className="space-y-4">
              <div className="rounded-xl px-4 py-3 text-[12.5px] leading-relaxed" style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A" }} data-testid="prod-pull-warning">
                <b>This REPLACES everything in preview</b> with an exact clone of production — org tree, wallets, all revenue history, withdrawals, portfolios, trainees, leads and login accounts. A safety backup of the current preview data is taken first. Production itself is never changed.
              </div>
              <div>
                <label className="text-[12px] font-semibold block mb-1" style={{ color: "#374151" }}>Production admin email</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="e.g. you@yourdomain.com" autoComplete="off"
                  className="w-full rounded-xl px-3.5 py-2.5 text-sm outline-none" style={{ border: "1.5px solid rgba(15,18,38,0.12)", background: "#F8FAFC" }} data-testid="prod-pull-email" />
              </div>
              <div>
                <label className="text-[12px] font-semibold block mb-1" style={{ color: "#374151" }}>Production admin password</label>
                <div className="relative">
                  <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="e.g. your production password" autoComplete="new-password"
                    className="w-full rounded-xl px-3.5 py-2.5 pr-10 text-sm outline-none" style={{ border: "1.5px solid rgba(15,18,38,0.12)", background: "#F8FAFC" }} data-testid="prod-pull-password" />
                  <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }}>
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                <div className="text-[11px] mt-1 flex items-center gap-1" style={{ color: "#64748B" }}>
                  <ShieldCheck size={12} /> Used once to sign in to production — never stored.
                </div>
              </div>
              <div>
                <label className="text-[12px] font-semibold block mb-1" style={{ color: "#374151" }}>Type <span className="font-mono" style={{ color: "#B91C1C" }}>REPLACE</span> to confirm</label>
                <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="e.g. REPLACE" autoComplete="off"
                  className="w-full rounded-xl px-3.5 py-2.5 text-sm outline-none font-mono" style={{ border: "1.5px solid rgba(15,18,38,0.12)", background: "#F8FAFC" }} data-testid="prod-pull-confirm-input" />
              </div>
              {startErr && <div className="text-[12.5px] font-medium" style={{ color: "#B91C1C" }} data-testid="prod-pull-start-error">{startErr}</div>}
              <button onClick={start} disabled={!canStart}
                className="w-full rounded-xl py-2.5 text-sm font-bold text-white transition-opacity disabled:opacity-40"
                style={{ background: "#B91C1C" }} data-testid="prod-pull-start-btn">
                Pull production into preview
              </button>
            </div>
          )}

          {(mode === "running" || mode === "done" || mode === "error") && (
            <div className="space-y-1.5" data-testid="prod-pull-progress">
              {PHASES.map(([key, label], i) => {
                const isDone = phaseIdx > i || status?.phase === "done";
                const isActive = status?.phase === key && status?.running;
                const failedHere = !!status?.error && phaseIdx === i;
                return (
                  <div key={key} className="flex items-center gap-2.5 px-3 py-2 rounded-lg" style={{ background: isActive ? "rgba(64,96,255,0.06)" : "transparent" }} data-testid={`prod-pull-phase-${key}`}>
                    {failedHere ? <AlertTriangle size={15} style={{ color: "#B91C1C" }} />
                      : isDone ? <CheckCircle2 size={15} style={{ color: "#00863A" }} />
                        : isActive ? <Loader2 size={15} className="animate-spin" style={{ color: "#4060FF" }} />
                          : <div className="w-[15px] h-[15px] rounded-full" style={{ border: "1.5px solid #CBD5E1" }} />}
                    <span className="text-[13px]" style={{ color: isDone || isActive ? "#0F1226" : "#94A3B8", fontWeight: isActive ? 600 : 400 }}>{label}</span>
                  </div>
                );
              })}

              {mode === "error" && (
                <div className="rounded-xl px-4 py-3 mt-3 text-[12.5px]" style={{ background: "#FEE2E2", color: "#991B1B", border: "1px solid #FECACA" }} data-testid="prod-pull-error">
                  <b>Pull failed — preview data may be unchanged (safety backup in /app/backups).</b>
                  <div className="mt-1">{status?.error}</div>
                </div>
              )}

              {mode === "done" && summary && (
                <div className="mt-3" data-testid="prod-pull-done">
                  <div className="rounded-xl px-4 py-3 text-[12.5px]" style={{ background: "#DCFCE7", color: "#14532D", border: "1px solid #BBF7D0" }}>
                    <b>Preview now mirrors production.</b> {summary.collections} collections · {summary.docs.toLocaleString()} documents · {summary.size_mb} MB snapshot.
                    <div className="mt-1">Login accounts were cloned too — sign in with your <b>production</b> password from now on.</div>
                  </div>
                  <div className="mt-2 max-h-36 overflow-y-auto rounded-xl px-3 py-2" style={{ background: "#F8FAFC", border: "1px solid rgba(15,18,38,0.08)" }}>
                    {Object.entries(summary.counts || {}).sort((a, b) => b[1] - a[1]).map(([c, n]) => (
                      <div key={c} className="flex justify-between text-[11.5px] py-0.5" style={{ color: "#475569" }}>
                        <span className="font-mono">{c}</span><span className="num font-semibold">{n.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => window.location.reload()} className="w-full rounded-xl py-2.5 mt-3 text-sm font-bold text-white" style={{ background: "#4060FF" }} data-testid="prod-pull-reload-btn">
                    Reload app
                  </button>
                </div>
              )}

              {mode === "running" && (
                <div className="text-[11.5px] mt-2 px-3" style={{ color: "#64748B" }}>
                  {status?.log?.length ? status.log[status.log.length - 1] : "Starting…"}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
